// serviceregistrationmodal.jsx
// -------------------------------------------------------------

import React, { useState, useEffect, useMemo } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import "../calendar.css";

// ──────────────────────────────────────────────
// PRICE MATRIX & HELPER
// ──────────────────────────────────────────────
const PRICE_MATRIX = {
  1: {
    "GIA-22": 15999,
    "SUP-22": 11999,
    "RIE-22": 24999,
    "GIA-23": 17999,
    "WHI-23": 0,
    "GIA-24": 17999,
    "MER-24": 17999,
  },
  2: {
    "GIA-22": 13999,
    "SUP-22": 9999,
    "RIE-22": 21999,
    "GIA-23": 14999,
    "WHI-23": 0,
    "GIA-24": 14999,
    "MER-24": 14999,
  },
  3: {
    "GIA-22": 11999,
    "SUP-22": 7999,
    "RIE-22": 19999,
    "GIA-23": 12999,
    "WHI-23": 0,
    "GIA-24": 12999,
    "MER-24": 12999,
  },
};

const calculatePrices = (priceCategory, skuPrefix) => {
  const matrixRow = PRICE_MATRIX[priceCategory];
  if (!matrixRow) return null;
  const incVat = matrixRow[skuPrefix] || 0;
  const exVat = incVat / 1.25;
  return { incVat, exVat };
};

// ──────────────────────────────────────────────────────────────
//  1.  Generic helpers & constants
// ──────────────────────────────────────────────────────────────

// Make React-Modal screen-reader friendly
ReactModal.setAppElement("#root");

/** Utility: return today in YYYY-MM-DD format */
const getCurrentDate = () => new Date().toISOString().split("T")[0];

/** Shared modal style */
const modalStyles = {
  content: {
    maxWidth: "1000px",
    width: "90%",
    margin: "auto",
    padding: "40px",
    borderRadius: "12px",
    border: "4px solid #374151",
    boxShadow: "0 12px 24px rgba(0,0,0,0.6)",
    overflow: "auto",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1000,
  },
};

/* ---------- LABOUR CONSTANTS (bookings only) ---------- */
const LABOUR_RATE = 1200; // NOK incl. VAT
const labourOptions = Array.from({ length: 10 }, (_, i) => i / 6);

// ──────────────────────────────────────────────────────────────
//  2.  Re-usable UI primitives
// ──────────────────────────────────────────────────────────────

/**
 * SectionCard – white card with rounded corners + shadow.
 * Accepts children and optional title.
 */
const SectionCard = ({ title, children, className = "" }) => (
  <section
    className={`bg-white rounded-xl border border-gray-200 shadow-lg p-6 ${className}`}
  >
    {title && (
      <h3 className="text-gray-700 font-semibold text-lg mb-4">{title}</h3>
    )}
    {children}
  </section>
);

/** Simple horizontal divider */
const Divider = ({ className = "" }) => (
  <hr className={`my-8 border-t border-gray-300 ${className}`} />
);

/**
 * PillButton – rounded pill with active / inactive states.
 * Used mainly for filters & simple selections.
 */
const PillButton = ({
  active,
  children,
  className = "",
  type = "button",
  ...props
}) => (
  <button
    type={type}
    {...props}
    className={`px-6 py-2 rounded-full font-semibold transition focus:outline-none ${
      active
        ? "bg-joule text-white shadow-md"
        : "bg-gray-200 text-gray-700 hover:bg-joule hover:text-white"
    } ${className}`}
  >
    {children}
  </button>
);

/**
 * Counter – plus/minus stepper identical to the one
 * in ReturnConfirmationModal. Keeps styling consistent.
 */
const Counter = ({ value, onChange, min = 0, max, className = "" }) => {
  const dec = () => onChange(Math.max(value - 1, min));
  const inc = () =>
    onChange(typeof max === "number" ? Math.min(value + 1, max) : value + 1);

  return (
    <div className={`inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={dec}
        className="h-8 w-8 flex items-center justify-center rounded-l-full bg-gray-200 hover:bg-joule hover:text-white font-bold"
      >
        –
      </button>
      <input
        readOnly
        value={value}
        className="w-12 h-8 text-center border-t border-b border-gray-300 font-semibold"
      />
      <button
        type="button"
        onClick={inc}
        className="h-8 w-8 flex items-center justify-center rounded-r-full bg-gray-200 hover:bg-joule hover:text-white font-bold"
      >
        +
      </button>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
//  3.  ServiceRegistrationForm – the heavy lifter UI
// ──────────────────────────────────────────────────────────────
const ServiceRegistrationForm = ({
  /* props passed from <ServiceCell /> */
  formData,
  handleChange,
  handleSubmit,
  toggleModalVisibility,
  errorMessage,
  isLoading,
  doNotSaveService,
  setDoNotSaveService,
  groupedParts,
  selectedParts,
  setSelectedParts,
  selectedAccessories,
  setSelectedAccessories,
  source,
  serviceHistory,
  expandedServices,
  toggleServiceExpansion,
  /* NEW – labour & case note (bookings only) */
  labourHours,
  setLabourHours,
  labourNote,
  setLabourNote,
}) => {
  /* ------------- local UI state ------------- */
  const [expandedGroups, setExpandedGroups] = useState({});

  /* ------------- handlers – parts ------------- */
  const handlePartSelection = (part, quantity) => {
    setSelectedParts((prev) => {
      const idx = prev.findIndex((p) => p.productNo === part.productNo);
      if (quantity === 0) {
        // remove if qty 0
        return prev.filter((p) => p.productNo !== part.productNo);
      }
      if (idx > -1) {
        // update existing (keep chargeCustomer flag)
        const next = [...prev];
        next[idx] = { ...next[idx], quantity };
        return next;
      }
      // add new (default chargeCustomer = false)
      return [...prev, { ...part, quantity, chargeCustomer: false }];
    });
  };

  /* NEW – toggle “Charge customer” flag (bookings only) */
  const toggleChargeCustomer = (productNo, charge) => {
    setSelectedParts((prev) =>
      prev.map((p) =>
        p.productNo === productNo ? { ...p, chargeCustomer: charge } : p
      )
    );
  };

  // helper to reuse existing handleChange API
  const setTireType = (val) =>
    handleChange({ target: { name: "tireType", value: val } });

  // Disable submit when tyres required but not selected (and when loading)
  const isTyreRequired = source === "orders" || source === "bookings";
  const tyreMissing = isTyreRequired && !formData.tireType;
  const priceCategoryMissing = source === "returns" && !formData.priceCategory;
  const isSubmitDisabled = isLoading || tyreMissing || priceCategoryMissing;

  /* ------------- handlers – accessories ------------- */
  const handleAccessorySelection = (name, quantity) => {
    setSelectedAccessories((prev) => {
      const idx = prev.findIndex((a) => a.name === name);
      if (quantity === 0) {
        return prev.filter((a) => a.name !== name);
      }
      if (idx > -1) {
        const next = [...prev];
        next[idx].quantity = quantity;
        return next;
      }
      return [...prev, { name, quantity }];
    });
  };

  /* ------------- helpers ------------- */
  const toggleGroup = (code) =>
    setExpandedGroups((p) => ({ ...p, [code]: !p[code] }));

  /** Memoised list of accessories – used for rendering */
  const accessoriesMeta = useMemo(
    () => [
      "charger",
      "adapter",
      "snakeLock",
      "foldingLock",
      "lockKey",
      "batteryKey",
      "reTyreSkins",
      "keyChain",
    ],
    []
  );

  // ──────────────────────────────────────────────
  //  RENDER
  // ──────────────────────────────────────────────
  return (
    <div>
      {/* ---------- Header ---------- */}
      <div className="text-center mb-8">
        <h2 className="text-gray-900 text-3xl font-semibold">
          Service Registration
        </h2>
        <p className="text-gray-600 text-lg font-medium mt-2">
          Service Type:&nbsp;
          <span className="text-gray-800 font-semibold">
            {formData.serviceType}
          </span>
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="text-base"
      >
        {/* =========================================================
         *  SERVICE HISTORY
         * =======================================================*/}
        {serviceHistory?.length > 0 && (
          <>
            <SectionCard title="🗂 Service History">
              <div className="overflow-auto">
                <table className="min-w-full bg-white divide-y divide-gray-200 text-gray-700 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">
                        Date
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        Category
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">KM</th>
                      <th className="px-4 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {serviceHistory.map((service) => (
                      <React.Fragment key={service._id}>
                        {/* clickable master row */}
                        <tr
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => toggleServiceExpansion(service._id)}
                        >
                          <td className="px-4 py-2">
                            {new Date(service.serviceDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2">
                            {service.serviceCategory}
                          </td>
                          <td className="px-4 py-2">{service.km}</td>
                          <td className="px-4 py-2 text-right">
                            <div
                              className={`inline-block w-3 h-3 rounded-full transition-transform ${
                                expandedServices[service._id]
                                  ? "bg-joule scale-125"
                                  : "bg-gray-400"
                              }`}
                            />
                          </td>
                        </tr>

                        {/* expandable detail row */}
                        {expandedServices[service._id] && (
                          <tr className="bg-gray-50">
                            <td colSpan={4} className="px-4 py-4">
                              {service.partsUsed?.length > 0 && (
                                <div className="mb-2">
                                  <strong>Parts Used:</strong>
                                  <ul className="list-disc list-inside ml-4">
                                    {service.partsUsed.map((part, i) => (
                                      <li key={i}>
                                        {part.productName} –{" "}
                                        {part.productDescription} (Qty:&nbsp;
                                        {part.quantity})
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {service.note && (
                                <p>
                                  <strong>Note:</strong>&nbsp;{service.note}
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            <Divider />
          </>
        )}

        {/* =========================================================
         *  SERVICE DETAILS (date, km, note…)
         * =======================================================*/}
        <SectionCard title="📝 Service Details" className="space-y-6">
          {/* --- Date --- */}
          <div>
            <label
              htmlFor="serviceDate"
              className="block text-gray-700 font-semibold mb-1"
            >
              Service Date
            </label>
            <input
              id="serviceDate"
              type="date"
              name="serviceDate"
              value={formData.serviceDate}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:border-joule focus:ring-2 focus:ring-joule"
            />
          </div>

          {/* --- Serial number (read-only) --- */}
          <div>
            <label
              htmlFor="serialNumber"
              className="block text-gray-700 font-semibold mb-1"
            >
              Serial Number
            </label>
            <input
              id="serialNumber"
              type="text"
              name="serialNumber"
              readOnly
              value={formData.serialNumber}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 bg-gray-100 focus:outline-none"
            />
          </div>

          {/* --- KM / ODO --- */}
          <div>
            <label
              htmlFor="km"
              className="block text-gray-700 font-semibold mb-1"
            >
              Kilometer / ODO
            </label>
            <input
              id="km"
              type="number"
              name="km"
              value={formData.km}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:border-joule focus:ring-2 focus:ring-joule"
            />
          </div>

          {/* --- Service Category --- */}
          <div>
            <label
              htmlFor="serviceCategory"
              className="block text-gray-700 font-semibold mb-1"
            >
              Service Category
            </label>
            <select
              id="serviceCategory"
              name="serviceCategory"
              value={formData.serviceCategory}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:border-joule focus:ring-2 focus:ring-joule"
            >
              <option value="">Select Category</option>
              <option value="refresh">Refresh</option>
              <option value="standard service">Standard service</option>
              <option value="full service">Full service</option>
              <option value="tire change">Tire change</option>
              <option value="corrective service">Corrective service</option>
            </select>
          </div>

          {/* --- Note --- */}
          <div>
            <label
              htmlFor="note"
              className="block text-gray-700 font-semibold mb-1"
            >
              Note
            </label>
            <textarea
              id="note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:border-joule focus:ring-2 focus:ring-joule"
            />
          </div>
        </SectionCard>

        <Divider />

        {/* =========================================================
         *  PARTS USED  – identical look to “Parts to Replace”
         *      • NEW: extra “Charge customer” check-box (bookings)
         * =======================================================*/}
        <SectionCard title="🛠️ Parts Used in Service">
          {Object.entries(groupedParts)
            .sort(
              ([a], [b]) =>
                (parseInt(a.match(/\d+/)?.[0] || "0", 10) || 0) -
                (parseInt(b.match(/\d+/)?.[0] || "0", 10) || 0)
            )
            .map(([groupCode, parts]) => {
              const groupName = parts[0]?.groupName ?? "";
              const isOpen = expandedGroups[groupCode];
              return (
                <div key={groupCode} className="mb-4">
                  {/* group toggle */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupCode)}
                    className="w-full flex items-center justify-between bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-md transition-colors"
                  >
                    <span className="font-semibold text-gray-800">
                      {groupCode} – {groupName}
                    </span>
                    <span
                      className={`transform transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    >
                      ▶
                    </span>
                  </button>

                  {/* inner list */}
                  {isOpen && (
                    <div className="mt-3 space-y-3">
                      {parts.map((part) => {
                        const sel = selectedParts.find(
                          (p) => p.productNo === part.productNo
                        );
                        return (
                          <div
                            key={part.productNo}
                            className="flex flex-wrap sm:flex-nowrap items-center bg-gray-50 hover:bg-white border border-gray-200 rounded-md p-2"
                          >
                            {/* select / deselect */}
                            <input
                              id={part.productNo}
                              type="checkbox"
                              className="form-checkbox h-5 w-5 accent-joule border-gray-300 rounded"
                              checked={!!sel}
                              onChange={(e) =>
                                handlePartSelection(
                                  part,
                                  e.target.checked ? 1 : 0
                                )
                              }
                            />
                            {/* part name */}
                            <label
                              htmlFor={part.productNo}
                              className="ml-3 flex-1 text-gray-700"
                            >
                              {part.productName}
                            </label>

                            {/* qty counter – visible only when selected */}
                            {sel && (
                              <Counter
                                value={sel.quantity}
                                onChange={(qty) =>
                                  handlePartSelection(part, qty)
                                }
                                className="ml-2"
                                min={1}
                              />
                            )}

                            {/* NEW – “Charge customer” flag (bookings only) */}
                            {sel && source === "bookings" && (
                              <div className="flex items-center ml-4">
                                <input
                                  id={`${part.productNo}-pay`}
                                  type="checkbox"
                                  className="form-checkbox h-5 w-5 accent-red-600 border-gray-300 rounded"
                                  checked={sel.chargeCustomer || false}
                                  onChange={(e) =>
                                    toggleChargeCustomer(
                                      part.productNo,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`${part.productNo}-pay`}
                                  className="ml-1 text-sm text-red-700 font-semibold"
                                >
                                  Charge customer
                                </label>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </SectionCard>

        {/* =========================================================
         *  LABOUR SECTION – bookings only
         * =======================================================*/}
        {source === "bookings" && (
          <>
            <Divider />
            <SectionCard title="⏱️ Labour – Select only if repairs are needed due to damage or abnormal use">
              <div className="mb-6">
                <p className="text-gray-700 font-medium mb-4">Select minutes</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 justify-center place-items-center gap-x-2 gap-y-4">
                  {labourOptions.map((opt) => (
                    <PillButton
                      key={opt}
                      active={labourHours === opt}
                      onClick={() => setLabourHours(opt)}
                    >
                      {`${Math.round(opt * 60)} min`}
                    </PillButton>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="labourNote"
                  className="block text-gray-700 text-sm font-semibold mt-2 mb-2"
                >
                  Labour Note
                </label>
                <textarea
                  id="labourNote"
                  value={labourNote}
                  onChange={(e) => setLabourNote(e.target.value)}
                  placeholder="Describe what was done / why customer should pay..."
                  className="border-gray-600 shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-joule focus:ring-1 focus:ring-joule"
                />
              </div>
            </SectionCard>
          </>
        )}

        {/* =========================================================
         *  ACCESSORIES – match “Missing Items” visual style
         * =======================================================*/}
        {source === "orders" && (
          <>
            <Divider />
            <SectionCard title="👜 Accessories Sending Out to Customer">
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-center rounded-t-md overflow-hidden">
                  <thead className="bg-gray-700 text-white">
                    <tr>
                      <th className="px-4 py-3 font-semibold text-sm uppercase tracking-wide">
                        Accessory
                      </th>
                      <th className="px-4 py-3 font-semibold text-sm uppercase tracking-wide">
                        Quantity to Send
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoriesMeta.map((key, idx) => {
                      const pretty = key.charAt(0).toUpperCase() + key.slice(1);
                      const currentQty =
                        selectedAccessories.find((a) => a.name === key)
                          ?.quantity || 0;
                      return (
                        <tr
                          key={key}
                          className={`border-b ${
                            idx % 2 === 1 ? "bg-gray-50" : ""
                          }`}
                        >
                          <td className="px-4 py-3 border-r font-medium">
                            {pretty}
                          </td>
                          <td className="px-4 py-3">
                            <Counter
                              value={currentQty}
                              onChange={(qty) =>
                                handleAccessorySelection(key, qty)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </>
        )}

        <Divider />

        {source === "returns" && (
          <>
            <Divider />
            <SectionCard title="💰 Price Category">
              <p className="text-gray-700 mb-8">
                Select the price category (1 = Good quality expensive, 3 = Poor
                quality cheap )
              </p>
              <div className="flex space-x-4 justify-center">
                {[1, 2, 3].map((num) => (
                  <PillButton
                    key={num}
                    active={formData.priceCategory === String(num)}
                    onClick={() =>
                      handleChange({
                        target: { name: "priceCategory", value: String(num) },
                      })
                    }
                  >
                    Category {num}
                  </PillButton>
                ))}
              </div>
            </SectionCard>
          </>
        )}

        <Divider />

        {/* =========================================================
         *  TYRE TYPE – shown for orders & bookings, required
         *  (uses PillButton styling like labour minutes)
         * =======================================================*/}
        {(source === "orders" || source === "bookings") && (
          <>
            <Divider />
            <SectionCard title="🚲 Tyre Type being sent out">
              <p className="text-gray-700 font-medium mb-4">
                Select which tyres the bike leaves with. (Required)
              </p>

              <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 justify-center place-items-center gap-x-2 gap-y-4">
                <PillButton
                  active={formData.tireType === "winter"}
                  onClick={() => setTireType("winter")}
                  aria-pressed={formData.tireType === "winter"}
                >
                  Winter tyre
                </PillButton>

                <PillButton
                  active={formData.tireType === "summer"}
                  onClick={() => setTireType("summer")}
                  aria-pressed={formData.tireType === "summer"}
                >
                  Summer tyre
                </PillButton>

                <PillButton
                  active={formData.tireType === "reTyre"}
                  onClick={() => setTireType("reTyre")}
                  aria-pressed={formData.tireType === "reTyre"}
                >
                  reTyre
                </PillButton>
              </div>
            </SectionCard>
          </>
        )}

        <Divider />

        {/* =========================================================
         *  ACTION BUTTONS + “Do not save” checkbox
         * =======================================================*/}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="submit"
            disabled={isSubmitDisabled}
            title={tyreMissing ? "Select tyre type first" : undefined}
            className={`w-full py-3 px-8 text-lg font-semibold rounded-lg shadow-md focus:outline-none ${
              isSubmitDisabled
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-joule text-white hover:bg-gray-900"
            }`}
          >
            {isLoading ? "Registering…" : "Register Service"}
          </button>

          <button
            type="button"
            className="w-full py-3 px-8 text-lg font-semibold rounded-lg shadow-md bg-red-500 text-white hover:bg-red-700 focus:outline-none"
            onClick={toggleModalVisibility}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>

        {/* “Do not save” toggle */}
        <div className="flex items-center mt-8">
          <input
            type="checkbox"
            checked={doNotSaveService}
            onChange={(e) => setDoNotSaveService(e.target.checked)}
            className="form-checkbox h-5 w-5 accent-joule border-gray-300 rounded"
          />
          <span className="ml-2 text-gray-700">Do not save service</span>
        </div>

        {/* Error message */}
        {errorMessage && (
          <div className="mt-4 text-red-600 text-center">{errorMessage}</div>
        )}
      </form>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
//  4.  ServiceCell – outer wrapper responsible for data I/O
// ──────────────────────────────────────────────────────────────
const ServiceCell = ({
  isOpen,
  data,
  source,
  toggleModalVisibility,
  markAsRepaired,
  reloadData,
}) => {
  /* -------- determine serviceType based on source -------- */
  let serviceType = "";
  if (source === "returns") serviceType = "Return Service";
  else if (source === "orders") serviceType = "Prepare Service";
  else if (source === "bookings")
    serviceType = data.type === "Service" ? "Booked Service" : "Booked Repair";
  else if (source === "itemPage") serviceType = "From stock";

  /* -------- main form state -------- */
  const [formData, setFormData] = useState({
    serviceDate: getCurrentDate(),
    serialNumber: data.serialNumber || "",
    km: data.km || "",
    note: data.note || "",
    serviceType,
    serviceCategory: "",
    orderId: data.orderId || "",
    subscriptionId: data.subscriptionId || "",
    tireType: "", // none pre-selected
    priceCategory: "",
  });

  /* -------- local state -------- */
  const [groupedParts, setGroupedParts] = useState({});
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [doNotSaveService, setDoNotSaveService] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [expandedServices, setExpandedServices] = useState({});

  /* NEW – labour (bookings only) */
  const [labourHours, setLabourHours] = useState(0);
  const [labourNote, setLabourNote] = useState("");

  /* -------- update formData on change -------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* -------- fetch compatible parts -------- */
  useEffect(() => {
    if (!formData.serialNumber) return;
    const fetchParts = async () => {
      try {
        // 1) get SKU (first 6 chars) from inventory
        const { data: inv } = await axios.get(
          `/api/inventory/${formData.serialNumber}`
        );
        const modelCode = inv?.model?.SKU?.slice(0, 6);
        if (!modelCode) return;

        // 2) fetch parts compatible with the model
        const { data: parts } = await axios.get(
          `/api/service-parts/${modelCode}`
        );

        // 3) group by groupCode for collapsible UI
        const grouped = parts.reduce((acc, p) => {
          acc[p.groupCode] = acc[p.groupCode] || [];
          acc[p.groupCode].push(p);
          return acc;
        }, {});
        setGroupedParts(grouped);
      } catch (err) {
        console.error("[ServiceRegistration] parts fetch error:", err);
      }
    };
    fetchParts();
  }, [formData.serialNumber]);

  /* -------- fetch service history -------- */
  useEffect(() => {
    if (!isOpen || !formData.serialNumber) return;
    axios
      .get(`/api/services/serial/${formData.serialNumber}`)
      .then((res) => setServiceHistory(res.data))
      .catch((err) =>
        console.error("[ServiceRegistration] history fetch error:", err)
      );
  }, [isOpen, formData.serialNumber]);

  /* -------- expand / collapse service history rows -------- */
  const toggleServiceExpansion = (id) =>
    setExpandedServices((p) => ({ ...p, [id]: !p[id] }));

  /* -------- form submit -------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // guard – basic required fields
    if (!doNotSaveService && (!formData.km || !formData.serviceCategory)) {
      setErrorMessage(
        "Please fill out both the KM and Service Category fields"
      );
      return;
    }

    // Tyre type is required for orders/bookings (always required to register)
    if ((source === "orders" || source === "bookings") && !formData.tireType) {
      setErrorMessage("Please select the tyre type (Winter, Summer or reTyre)");
      return;
    }

    setIsLoading(true);

    try {
      /* ------------------------------------------------------
       * 0. Customer case (bookings only, if chargeable items)
       * ---------------------------------------------------- */
      if (source === "bookings") {
        const chargeableParts = selectedParts.filter((p) => p.chargeCustomer);
        const partLines = chargeableParts.map((p) => ({
          lineType: "part",
          productNo: p.productNo,
          productName: p.productName,
          productDescription: p.productDescription,
          quantity: p.quantity,
          salesPriceIncVat: p.salesPriceIncVat,
          lineTotal: p.salesPriceIncVat * p.quantity,
          status: "open",
        }));

        const labourLines =
          labourHours > 0
            ? [
                {
                  lineType: "labour",
                  productName: "Arbeidstid mekaniker",
                  hours: labourHours,
                  quantity: 1,
                  salesPriceIncVat: LABOUR_RATE,
                  lineTotal: LABOUR_RATE * labourHours,
                  status: "open",
                  note: labourNote,
                },
              ]
            : [];

        const caseLines = [...partLines, ...labourLines];

        if (caseLines.length > 0) {
          await axios.post("/api/customer-cases", {
            serialNumber: formData.serialNumber,
            subscriptionId: data.subscriptionId,
            orderId: data.orderId,
            bookingId: data._id, // NEW: store booking _id on the case
            fullName: `${data.firstName} ${data.lastName}`,
            phone: data.number,
            email: data.email,
            source: "Service",
            note: labourNote,
            caseLines,
          });
        }
      }

      /* ------------------------------------------------------
       * 1.  Update bike operational status
       * ---------------------------------------------------- */
      let statusPayload;
      if (source === "returns")
        statusPayload = { operationalStatus: "in stock" };
      else if (["orders", "bookings"].includes(source))
        statusPayload = { operationalStatus: "ready for customer" };

      if (statusPayload)
        await axios.put(
          `/api/inventory/${formData.serialNumber}/operationalStatus`,
          statusPayload
        );

      /* ------------------------------------------------------
       * 1.5  Save tyre type (orders & bookings)
       * ---------------------------------------------------- */
      if (source === "orders" || source === "bookings") {
        await axios.put(`/api/inventory/${formData.serialNumber}/tireType`, {
          tireType: formData.tireType,
        });
      }

      /* ------------------------------------------------------
       * 2.  Save service (unless “do not save”)
       * ---------------------------------------------------- */
      if (!doNotSaveService) {
        await axios.post("/api/services", {
          ...formData,
          partsUsed: selectedParts,
        });
        // update last service info on inventory
        await axios.put(`/api/inventory/${formData.serialNumber}`, {
          lastService: formData.serviceDate,
          lastKm: parseInt(formData.km, 10),
        });
      }

      /* ------------------------------------------------------
       * 3.  Accessories (orders only)
       * ---------------------------------------------------- */
      if (source === "orders") {
        const accessoriesPayload = selectedAccessories.reduce((acc, a) => {
          acc[a.name] = a.quantity;
          return acc;
        }, {});
        await axios.put(
          `/api/inventory/${formData.serialNumber}/accessories`,
          accessoriesPayload
        );
      }

      /* ------------------------------------------------------
       * 4.  Rackbeat stock adjustment for parts
       * ---------------------------------------------------- */
      await Promise.all(
        selectedParts.map((p) =>
          axios.post("/api/rackbeat/adjust-stock", {
            productNumber: p.productNo,
            quantity: -p.quantity,
            location: 1002,
            date: formData.serviceDate,
          })
        )
      );

      /* ------------------------------------------------------
       * 5.  Misc. business logic for different sources
       * ---------------------------------------------------- */
      if (source === "bookings" && data.type !== "winter_freeze") {
        await axios.post("/api/send-service-ready-sms", {
          number: data.number,
          bookingId: data._id,
        });
      }

      if (source === "orders") {
        await axios.put(`/api/circuly-orders/${data.orderId}/tag`, {
          tag: "waiting for dispatch",
        });
      }

      if (source === "returns") {
        // COMMENTED OUT: Circuly call
        // await axios.post(`/api/set-in-stock/${formData.serialNumber}`);

        // --- NEW: calculate and update purchase price based on priceCategory and SKU prefix ---
        const invRes = await axios.get(
          `/api/inventory/${formData.serialNumber}`
        );
        const skuPrefix = invRes.data?.model?.SKU?.slice(0, 6);
        const category = parseInt(formData.priceCategory, 10);

        if (!skuPrefix || !category) {
          console.warn(
            "Missing SKU prefix or price category – skipping price update."
          );
        } else {
          const prices = calculatePrices(category, skuPrefix);
          if (prices && prices.incVat > 0) {
            // 👉 new route here
            await axios.put(
              `/api/inventory/${formData.serialNumber}/buyout-price`,
              {
                purchasePriceIncVat: prices.incVat,
                purchasePriceExVat: prices.exVat,
                priceCategory: category, // store category in inventory too
              }
            );
            console.log(
              `✅ Updated ${formData.serialNumber} – ${
                prices.incVat
              } incl. VAT (${prices.exVat.toFixed(2)} ex VAT)`
            );
          } else {
            console.warn("No matching price found for SKU prefix:", skuPrefix);
          }
        }
      }

      if (typeof markAsRepaired === "function") await markAsRepaired(data._id);
      if (typeof reloadData === "function") await reloadData();

      toggleModalVisibility();
    } catch (err) {
      setErrorMessage(
        `Error: ${err.response?.data?.error || err.message || "Unknown"}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ──────────────────────────────────────────────
  //  Modal container
  // ──────────────────────────────────────────────
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={toggleModalVisibility}
      contentLabel="Service Registration"
      style={modalStyles}
    >
      <ServiceRegistrationForm
        /* ––– pass everything down ––– */
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        toggleModalVisibility={toggleModalVisibility}
        errorMessage={errorMessage}
        isLoading={isLoading}
        doNotSaveService={doNotSaveService}
        setDoNotSaveService={setDoNotSaveService}
        groupedParts={groupedParts}
        selectedParts={selectedParts}
        setSelectedParts={setSelectedParts}
        selectedAccessories={selectedAccessories}
        setSelectedAccessories={setSelectedAccessories}
        source={source}
        serviceHistory={serviceHistory}
        expandedServices={expandedServices}
        toggleServiceExpansion={toggleServiceExpansion}
        /* NEW – labour props */
        labourHours={labourHours}
        setLabourHours={setLabourHours}
        labourNote={labourNote}
        setLabourNote={setLabourNote}
      />
    </ReactModal>
  );
};

export default ServiceCell;
